import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

export type BadgeVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'teal';
export type BadgeSize = 'sm' | 'md';
/**
 * Badge shape:
 * - 'default' — rounded-control rectangular badge (standard inline label)
 * - 'status'  — rounded-full pill with uppercase tracking (KPI / status chip per rulebook §5)
 */
export type BadgeShape = 'default' | 'status';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  /** 'status' renders a rounded-full pill with uppercase letter-spacing (caption scale / 700). */
  shape?: BadgeShape;
  children?: ReactNode;
}

// Status badges use the pale/text pair per DESIGN.md: the background tint
// carries meaning, no border. Saturated fill is reserved for rare emphasis.
const VARIANT: Record<BadgeVariant, string> = {
  // text-secondary, not text-muted -- text-muted on bg-surface-deep computes
  // to 4.01:1 light / 4.07:1 dark, both below this product's own 4.5:1 AA
  // floor for de-emphasized text (PRODUCT.md "Accessibility & Inclusion").
  // text-secondary clears it (6.11:1 light / 4.57:1 dark) and is the SAME
  // pairing the Table primitive already uses for this exact background.
  neutral: 'bg-surface-deep text-secondary ring-1 ring-inset ring-border',
  success: 'bg-success-pale text-success-text ring-1 ring-inset ring-success/20',
  warning: 'bg-warning-pale text-warning-text ring-1 ring-inset ring-warning/20',
  danger: 'bg-danger-pale text-danger-text ring-1 ring-inset ring-danger/20',
  info: 'bg-info-pale text-info-text ring-1 ring-inset ring-info/20',
  teal: 'bg-teal-pale text-teal-text ring-1 ring-inset ring-teal/20',
};

const SIZE: Record<BadgeSize, string> = {
  sm: 'text-caption px-1.5 py-0.5',
  md: 'text-body-sm px-2 py-0.5',
};

// Shape overrides the border-radius; status uses rounded-full + uppercase tracking.
const SHAPE: Record<BadgeShape, string> = {
  default: 'rounded-control',
  status: 'rounded-full uppercase tracking-wide font-bold',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = 'neutral', size = 'sm', shape = 'default', className, ...rest },
  ref,
) {
  const classes = [
    'inline-flex items-center',
    SHAPE[shape],
    VARIANT[variant],
    SIZE[size],
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return <span ref={ref} className={classes} {...rest} />;
});
